/* Conexão com o Google (Gmail somente leitura). Estado compartilhado por E-mail,
   Configurações, barra lateral e tela inicial. */
import { useSyncExternalStore } from 'react';
import type { GoogleStatus } from '@jarvis/shared';
import { api } from './api';

let status: GoogleStatus | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const google = {
  get: () => status,
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },
  set(s: GoogleStatus) { status = s; emit(); },
  load(): Promise<void> {
    loading ??= api.get<GoogleStatus>('/api/google/status')
      .then((s) => { status = s; }, () => { /* sem servidor: fica como estava */ })
      .finally(() => { loading = null; emit(); });
    return loading;
  },
  /* Navega para o Google; a volta acontece em /api/google/callback. */
  connect() { window.location.assign('/api/google/connect'); },
  async disconnect() {
    const r = await api.post<{ revoked: boolean; emails: number }>('/api/google/disconnect');
    await google.load();
    return r;
  }
};

export function useGoogle(): GoogleStatus | null {
  return useSyncExternalStore(google.subscribe, google.get);
}

const REASONS: Record<string, string> = {
  recusado: 'Você não autorizou o acesso na tela do Google. Nada foi conectado.',
  sessao: 'A conexão expirou ou foi concluída em outro navegador. Tente de novo, neste mesmo navegador.',
  nao_configurado: 'As credenciais do Google ainda não estão cadastradas na Render (Parte F do guia).',
  google_scope_too_broad: 'O Google concedeu permissões além da leitura, e o Jarvis recusou por segurança. Confira em Data Access se só o escopo gmail.readonly está marcado.',
  google_scope_missing: 'A permissão de ler o Gmail não foi marcada na tela do Google. Tente de novo e deixe a opção marcada.',
  google_client_invalid: 'O Google recusou as credenciais do Jarvis. Confira GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET na Render.',
  google_no_refresh: 'O Google não enviou a autorização duradoura. Tente conectar de novo.',
  google_token_error: 'O Google recusou a troca do código. Confira se o endereço de retorno (redirect URI) é idêntico no Google Cloud e na Render.',
  gmail_forbidden: 'O Gmail recusou o acesso. Confira se a Gmail API está ativada no projeto do Google Cloud.'
};

/* Lê o resultado da volta do Google (?google=...) uma vez e limpa o endereço. */
export function takeGoogleNotice(): { ok: boolean; text: string } | null {
  const p = new URLSearchParams(window.location.search);
  const v = p.get('google');
  if (!v) return null;
  history.replaceState(null, '', window.location.pathname + window.location.hash);
  if (v === 'conectado') return { ok: true, text: 'Gmail conectado, só para leitura.' };
  const motivo = p.get('motivo') ?? '';
  return { ok: false, text: REASONS[motivo] ?? `Não foi possível conectar o Gmail (${motivo || 'erro desconhecido'}).` };
}
